/**
 * `<video>`의 상태를 바꾸지 않고, 현재 위치에서 재생을 이어갈 수 있는지 판정한다.
 * `useSyncedVideoTimeline`은 이 판정으로 여러 영상의 공통 재생 시간을 멈추거나 다시 진행한다.
 *
 * 네트워크 대기는 `paused`로 드러나지 않는다 — 브라우저는 `paused=false`인 채 `waiting`을 내고
 * `readyState`를 낮춘다. 그래서 판정은 `readyState`·`seeking`·`buffered`·`networkState`를 본다. `stalled` 이벤트는
 * Chrome이 내지 않아 쓰지 않는다.
 */

/** 현재 위치의 프레임을 그릴 데이터가 있다. */
export const HAVE_CURRENT_DATA = 2;
/** 현재 위치 다음 프레임까지 디코드할 데이터가 있다. */
export const HAVE_FUTURE_DATA = 3;
/** 브라우저가 영상 소스를 선택했지만 현재 데이터를 받지 않는 상태다. */
export const NETWORK_IDLE = 1;

// 위치를 덮는 range를 찾을 때 range 시작이 위치보다 이만큼 뒤여도 덮는다고 본다. Chrome의
// `buffered`는 받은 바이트를 시간으로 선형 환산한 근사라 경계가 한 프레임 남짓 어긋날 수 있다.
const BUFFERED_RANGE_START_TOLERANCE_SECONDS = 0.05;
// 앞선 초가 목표에 이만큼 못 미쳐도 채운 것으로 본다(끝 경계의 부동소수 오차).
const BUFFERED_AHEAD_TOLERANCE_SECONDS = 0.001;

/** `seconds`를 덮는 buffered range가 그 위치보다 몇 초 앞까지 이어지는가. 덮는 range가 없으면 0. */
export function bufferedAheadSeconds(buffered: TimeRanges, seconds: number): number {
  for (let index = 0; index < buffered.length; index += 1) {
    const start = buffered.start(index);
    const end = buffered.end(index);
    if (start <= seconds + BUFFERED_RANGE_START_TOLERANCE_SECONDS && end > seconds) {
      return end - seconds;
    }
  }
  return 0;
}

/** 기다려도 데이터가 오지 않는 채널 — 오류로 멈췄거나 불러올 소스가 없다. 판정에서 뺀다. */
export function isMediaUnavailable(video: HTMLVideoElement): boolean {
  return video.error != null || (!video.currentSrc && !video.getAttribute("src"));
}

/** 지금 위치에서 다음 프레임을 그릴 수 없다 — 데이터를 받는 중이거나 seek를 끝내지 못했다. */
export function isMediaStarved(video: HTMLVideoElement): boolean {
  if (video.ended) return false;
  return video.readyState < HAVE_FUTURE_DATA || video.seeking;
}

/**
 * 버퍼 대기 중인 채널이 재생을 시작할 수 있는지 판정한다.
 * 다음 프레임을 표시할 수 있어야 하며, 재생 위치 이동(seek)이 끝나야 한다.
 * `seconds` 이후의 영상 데이터를 목표 시간만큼 받았거나 다운로드를 멈춘 상태(NETWORK_IDLE)이면 재생할 수 있다.
 *
 * 일시정지된 `<video>`는 앞으로 재생할 데이터를 일정량 받으면 다운로드를 멈출 수 있다.
 * NETWORK_IDLE에서는 목표보다 적게 받았어도 재생을 시작한다. 재생과 추가 다운로드가 서로를 기다리지 않게 하기 위해서다.
 */
export function isMediaReadyToPlay(
  video: HTMLVideoElement,
  {
    seconds,
    aheadSeconds,
    endSeconds,
  }: Readonly<{ seconds: number; aheadSeconds: number; endSeconds: number }>,
): boolean {
  if (isMediaStarved(video)) return false;
  const required = Math.max(0, Math.min(aheadSeconds, endSeconds - seconds));
  return (
    bufferedAheadSeconds(video.buffered, seconds) >= required - BUFFERED_AHEAD_TOLERANCE_SECONDS ||
    video.networkState === NETWORK_IDLE
  );
}
